import React, { useState, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import AnimatedNumber from 'animated-number-react';
import { ColorRing } from 'react-loader-spinner';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { miceActivityDonutChartOptions } from './chartOptions';
import MiceActivityChartTitle from './MiceActivityChartTitle';

import PeriodLabel from 'components/AnalyticsPage/PeriodLabel';
import { Period } from 'components/AnalyticsPage/Periods';
// eslint-disable-next-line import/order
import useGetMiceActivityDonutChartData from 'hooks/Analitics/MCTCharts/3_miceActivityDonutChart/useGetMiceActivityDonutChartData';

// eslint-disable-next-line import/order
import { transformDataToPiePeriodChart } from 'utils/transformDataToPiePeriodChart';

import './3miceActivityDonutChart.scss';
import { TrapType } from 'components/AnalyticsPage/TrapType';
import { ObjectTraps } from 'api/analitics/types';

interface ChartProps {
  slug: string;
  type: TrapType;
  objectTraps: ObjectTraps;
}

const MiceActivityDonutChart: React.FC<ChartProps> = ({ slug, type, objectTraps }) => {
  const [trapType, setTrapType] = useState<TrapType>(type === TrapType.ALL ? TrapType.MCT : type);
  const [period, setPeriod] = useState<Period>(Period.THREE_MONTH);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [showCustom, setShowCustom] = useState<boolean>(false);
  const { t } = useTranslation();

  // Initialize dates based on default period
  useEffect(() => {
    const threeMonthsAgo = dayjs().subtract(3, 'month').format('YYYY-MM-DD');
    const today = dayjs().format('YYYY-MM-DD');
    setStartDate(threeMonthsAgo);
    setEndDate(today);
  }, []);

  const { data, isLoading, isFetching, refetch } = useGetMiceActivityDonutChartData(
    slug,
    trapType,
    startDate,
    endDate,
    !!startDate && !!endDate,
  );
  const chartData = transformDataToPiePeriodChart(data || []);

  const chartOptions = miceActivityDonutChartOptions(chartData);

  return (
    <div className="card card-height-100 m-0 border border-2 rounded-4 position-relative">
      <div className="card-header rounded-4">
        <MiceActivityChartTitle
          title={
            trapType === TrapType.SRST
              ? t('ChartsAnalitics.mice_activity_pie_chart.title')
              : trapType === TrapType.MCT
                ? t('ChartsAnalitics.mice_activity_pie_chart.title_mct')
                : t('ChartsAnalitics.mice_activity_pie_chart.title_smst')
          }
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          period={period}
          setPeriod={setPeriod}
          showCustom={showCustom}
          setShowCustom={setShowCustom}
          refetch={refetch}
          showSwitchTrapType={
            objectTraps.mct.was_ever_installed && objectTraps.smst.was_ever_installed && type !== TrapType.SRST
          }
          switchTrapType={trapType}
          setSwitchTrapType={(value: TrapType) => setTrapType(value)}
          switchTrapTypeLoading={isFetching}
        />
      </div>
      <div className="card-body">
        <div className="position-relative">
          {isLoading || isFetching ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '350px' }}>
              <ColorRing
                visible
                height="80"
                width="80"
                ariaLabel="color-ring-loading"
                wrapperStyle={{}}
                wrapperClass="color-ring-wrapper"
                colors={['#9C7AE2', '#E27A8C', '#C0E27A', '#7AE2D0', '#F7F18F']}
              />
            </div>
          ) : (
            <>
              <div className="d-flex flex-column flex-md-row align-items-center">
                <div className="d-flex align-items-center">
                  <h4 className="mb-0">{t('ChartsAnalitics.mice_activity_pie_chart.total')}</h4>
                  <span className="badge rounded-pill border border-danger text-danger mx-2 d-flex align-items-center">
                    <AnimatedNumber
                      value={chartData.series.reduce((a, b) => a + b, 0)}
                      formatValue={number => number.toLocaleString('en-US', { useGrouping: true }).replace(/,/g, ' ')}
                      duration={1000}
                      className="animated-number fs-16"
                    />
                  </span>
                </div>
                <PeriodLabel period={period} customStart={startDate} customEnd={endDate} />
              </div>
              <div id="chart">
                <ReactApexChart options={chartOptions} series={chartOptions.series} type="donut" height={600} />
              </div>
              <div id="html-dist" />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MiceActivityDonutChart;
